export const reccImplementationDataPart2 = {
  "2.3212": {
    title: "Optimise plant power factor",
    description: "Improve electrical power factor through capacitor installation and power factor correction to reduce electrical demand charges.",
    keyLevers: [
      {
        category: "Instrumentation and Measurement",
        actions: [
          "Monitor power factor continuously across facility",
          "Measure reactive power demand by equipment",
          "Analyze electrical load patterns and power quality"
        ]
      },
      {
        category: "Control System Upgrades",
        actions: [
          "Install automatic power factor correction systems",
          "Add capacitor banks with switching controls",
          "Implement harmonic filtering where needed"
        ]
      },
      {
        category: "Operational Practices",
        actions: [
          "Monitor power factor performance regularly",
          "Coordinate capacitor operation with load changes",
          "Train electrical staff on power factor management"
        ]
      },
      {
        category: "Maintenance and Efficiency Measures",
        actions: [
          "Maintain capacitor banks and switching equipment",
          "Test power factor correction equipment regularly",
          "Replace failed capacitors promptly"
        ]
      },
      {
        category: "Digital and Data Use",
        actions: [
          "Track power factor trends and demand charges",
          "Analyze power quality data",
          "Monitor capacitor performance and failures"
        ]
      }
    ],
    costCategories: {
      lowCost: ["Install fixed capacitor banks", "Improve equipment power factor"],
      mediumCost: ["Automatic power factor correction systems", "Harmonic filtering equipment"],
      highCost: ["Advanced power quality management systems", "Facility-wide power factor optimization"]
    },
    riskFactors: ["Harmonic distortion from power electronics", "Capacitor failures and safety hazards", "Over-correction leading to leading power factor"]
  },
  "2.4111": {
    title: "Utilise energy-efficient belts and other improved drive systems",
    description: "Replace standard V-belts with high-efficiency belts and optimize drive system design for improved power transmission.",
    keyLevers: [
      {
        category: "Instrumentation and Measurement",
        actions: [
          "Measure motor power consumption and efficiency",
          "Assess belt tension and alignment",
          "Calculate drive system losses and efficiency"
        ]
      },
      {
        category: "Control System Upgrades",
        actions: [
          "Install synchronous belts or high-efficiency V-belts",
          "Optimize pulley ratios for system efficiency",
          "Implement direct drive systems where applicable"
        ]
      },
      {
        category: "Operational Practices",
        actions: [
          "Maintain proper belt tension",
          "Align drives properly during installation",
          "Train maintenance staff on efficient drive systems"
        ]
      },
      {
        category: "Maintenance and Efficiency Measures",
        actions: [
          "Replace worn belts with efficient alternatives",
          "Maintain proper belt tension and alignment",
          "Lubricate bearings and check wear regularly"
        ]
      },
      {
        category: "Digital and Data Use",
        actions: [
          "Monitor drive system efficiency",
          "Track belt replacement schedules",
          "Analyze power consumption trends"
        ]
      }
    ],
    costCategories: {
      lowCost: ["Replace standard belts with high-efficiency belts", "Improve belt maintenance"],
      mediumCost: ["Install synchronous belt drives", "Optimize pulley systems"],
      highCost: ["Convert to direct drive systems", "Complete drive system redesign"]
    },
    riskFactors: ["Belt failure during operation", "Improper installation reduces efficiency", "Higher upfront cost for premium belts"]
  },
  "2.4133": {
    title: "Use most efficient type of electric motors",
    description: "Replace standard motors with premium efficiency motors and right-size motors for their applications.",
    keyLevers: [
      {
        category: "Instrumentation and Measurement",
        actions: [
          "Conduct motor efficiency testing",
          "Measure actual motor loading",
          "Analyze motor power consumption patterns"
        ]
      },
      {
        category: "Control System Upgrades",
        actions: [
          "Install premium efficiency motors",
          "Right-size motors for actual loads",
          "Use electronically commutated motors where applicable"
        ]
      },
      {
        category: "Operational Practices",
        actions: [
          "Maintain motors properly to preserve efficiency",
          "Operate motors near optimal loading",
          "Train staff on efficient motor operation"
        ]
      },
      {
        category: "Maintenance and Efficiency Measures",
        actions: [
          "Maintain motor alignment and balance",
          "Keep motors clean and well-ventilated",
          "Replace failed motors with high-efficiency models"
        ]
      },
      {
        category: "Digital and Data Use",
        actions: [
          "Monitor motor efficiency and performance",
          "Track motor operating hours and loading",
          "Analyze motor replacement opportunities"
        ]
      }
    ],
    costCategories: {
      lowCost: ["Improve motor maintenance practices", "Right-size oversized motors"],
      mediumCost: ["Replace motors with premium efficiency models", "Install high-efficiency motor controls"],
      highCost: ["Facility-wide motor efficiency upgrade program"]
    },
    riskFactors: ["Higher upfront cost for efficient motors", "Motor failure disrupts operations", "Efficiency gains may not justify replacement of working motors"]
  },
  "2.4146": {
    title: "Use adjustable frequency drive or multiple speed motors",
    description: "Install variable frequency drives (VFDs) or multi-speed motors to match motor speed with load requirements.",
    keyLevers: [
      {
        category: "Instrumentation and Measurement",
        actions: [
          "Analyze load profiles and speed requirements",
          "Measure power consumption at various loads",
          "Assess motor speed control opportunities"
        ]
      },
      {
        category: "Control System Upgrades",
        actions: [
          "Install variable frequency drives on appropriate applications",
          "Use multi-speed motors for discrete speed control",
          "Implement soft starters to reduce starting current"
        ]
      },
      {
        category: "Operational Practices",
        actions: [
          "Optimize VFD programming for applications",
          "Train operators on VFD controls and benefits",
          "Coordinate VFD operation with process requirements"
        ]
      },
      {
        category: "Maintenance and Efficiency Measures",
        actions: [
          "Maintain VFD cooling and ventilation",
          "Inspect and clean VFD components regularly",
          "Monitor VFD performance and efficiency"
        ]
      },
      {
        category: "Digital and Data Use",
        actions: [
          "Monitor VFD energy savings",
          "Track motor speed and power consumption",
          "Analyze VFD performance data"
        ]
      }
    ],
    costCategories: {
      lowCost: ["Install VFDs on high-use, variable load motors", "Optimize existing VFD settings"],
      mediumCost: ["Retrofit motors with multi-speed capability", "Install VFDs on medium-size motors"],
      highCost: ["Facility-wide VFD implementation program"]
    },
    riskFactors: ["VFD harmonics affect power quality", "Complex programming and setup", "Higher maintenance requirements for VFDs"]
  },
  "2.4221": {
    title: "Install compressor air intakes in coolest location",
    description: "Relocate compressor air intakes to draw cooler ambient air, reducing compression energy requirements.",
    keyLevers: [
      {
        category: "Instrumentation and Measurement",
        actions: [
          "Measure ambient temperatures at potential intake locations",
          "Monitor compressor inlet temperatures",
          "Calculate energy savings from cooler intake air"
        ]
      },
      {
        category: "Control System Upgrades",
        actions: [
          "Relocate intake ducts to coolest available locations",
          "Install intake air cooling systems where beneficial",
          "Use building ventilation to provide cool intake air"
        ]
      },
      {
        category: "Operational Practices",
        actions: [
          "Monitor seasonal temperature variations",
          "Maintain clear air intake paths",
          "Coordinate intake modifications with compressor maintenance"
        ]
      },
      {
        category: "Maintenance and Efficiency Measures",
        actions: [
          "Keep intake filters clean",
          "Maintain intake ducting and connections",
          "Inspect for air leaks in intake systems"
        ]
      },
      {
        category: "Digital and Data Use",
        actions: [
          "Monitor compressor efficiency vs intake temperature",
          "Track energy consumption with cooler intake air",
          "Analyze seasonal performance variations"
        ]
      }
    ],
    costCategories: {
      lowCost: ["Relocate existing intake connections", "Improve intake air path"],
      mediumCost: ["Install new intake ducting systems", "Add intake air cooling"],
      highCost: ["Complete compressor room ventilation redesign"]
    },
    riskFactors: ["Contamination from new intake locations", "Increased ducting complexity", "Weather protection requirements"]
  },
  "2.4231": {
    title: "Eliminate unnecessary compressed air uses",
    description: "Implement policies and controls to eliminate non-essential compressed air usage for cooling, sweeping, and other applications.",
    keyLevers: [
      {
        category: "Instrumentation and Measurement",
        actions: [
          "Survey all compressed air points of use",
          "Install flow meters or data loggers to quantify non-essential usage",
          "Benchmark consumption against industry standards (SCFM per unit output)"
        ]
      },
      {
        category: "Control System Upgrades",
        actions: [
          "Install solenoid valves, timers, or interlocks to prevent continuous non-essential use",
          "Provide dedicated electric fans, blowers, or vacuum systems for approved applications"
        ]
      },
      {
        category: "Operational Practices",
        actions: [
          "Implement clear policy banning sweeping and cooling with compressed air",
          "Train operators on alternative cleaning and cooling methods",
          "Develop Standard Operating Procedures (SOPs) for approved vs non-approved uses"
        ]
      },
      {
        category: "Maintenance and Efficiency Measures",
        actions: [
          "Remove unauthorized hoses and nozzles from shop floors",
          "Replace inefficient nozzles with engineered air knives or low-energy blowers",
          "Conduct regular audits to ensure policy compliance"
        ]
      },
      {
        category: "Digital and Data Use",
        actions: [
          "Analyze compressed air usage trends by department and shift",
          "Establish KPIs for reducing non-essential consumption",
          "Integrate monitoring data into dashboards for visibility"
        ]
      }
    ],
    costCategories: {
      lowCost: ["Ban compressed air sweeping/cooling", "Provide low-cost tools (brooms, vacuums, fans)"],
      mediumCost: ["Install dedicated electric blowers, vacuums, fans", "Add timers or solenoids"],
      highCost: ["Redesign processes for permanent elimination", "Integrate automation with mechanical systems"]
    },
    riskFactors: ["Workforce resistance to changing established practices", "Initial investment required for alternative equipment", "Potential temporary productivity disruption during transition"]
  },
  "2.4236": {
    title: "Eliminate leaks in inert gas and compressed air lines/valves",
    description: "Systematically identify and repair leaks in compressed air and inert gas systems to reduce energy waste.",
    keyLevers: [
      {
        category: "Instrumentation and Measurement",
        actions: [
          "Conduct ultrasonic leak detection surveys",
          "Quantify leak rates and energy costs",
          "Map leak locations throughout facility"
        ]
      },
      {
        category: "Control System Upgrades",
        actions: [
          "Install automatic leak detection systems",
          "Use high-quality fittings and connections",
          "Implement proper pipe sizing and routing"
        ]
      },
      {
        category: "Operational Practices",
        actions: [
          "Establish regular leak detection schedules",
          "Train maintenance staff on leak repair techniques",
          "Tag and track leaks until repaired"
        ]
      },
      {
        category: "Maintenance and Efficiency Measures",
        actions: [
          "Repair identified leaks promptly",
          "Replace worn valves and fittings",
          "Maintain proper pipe supports and connections"
        ]
      },
      {
        category: "Digital and Data Use",
        actions: [
          "Track leak repair progress and costs",
          "Monitor system pressure trends",
          "Analyze leak occurrence patterns"
        ]
      }
    ],
    costCategories: {
      lowCost: ["Repair small leaks with basic materials", "Improve maintenance practices"],
      mediumCost: ["Replace major leak sources", "Install leak detection equipment"],
      highCost: ["Replace entire piping sections", "Upgrade to leak-resistant systems"]
    },
    riskFactors: ["Safety hazards from pressurized systems", "Production disruption during repairs", "Hidden leaks in inaccessible locations"]
  },
  "2.4239": {
    title: "Eliminate or reduce compressed air usage",
    description: "Replace compressed air applications with more efficient alternatives where technically feasible.",
    keyLevers: [
      {
        category: "Instrumentation and Measurement",
        actions: [
          "Audit all compressed air applications",
          "Measure compressed air consumption by application",
          "Calculate cost of compressed air vs alternatives"
        ]
      },
      {
        category: "Control System Upgrades",
        actions: [
          "Install electric motors instead of pneumatic drives",
          "Use blowers for low-pressure applications",
          "Implement vacuum systems for material handling"
        ]
      },
      {
        category: "Operational Practices",
        actions: [
          "Evaluate process requirements vs compressed air capability",
          "Train operators on alternative technologies",
          "Phase out unnecessary compressed air applications"
        ]
      },
      {
        category: "Maintenance and Efficiency Measures",
        actions: [
          "Maintain alternative systems properly",
          "Remove disconnected compressed air equipment",
          "Optimize remaining compressed air systems"
        ]
      },
      {
        category: "Digital and Data Use",
        actions: [
          "Monitor energy consumption of alternative systems",
          "Track compressed air reduction progress",
          "Analyze cost benefits of alternatives"
        ]
      }
    ],
    costCategories: {
      lowCost: ["Eliminate simple compressed air applications", "Use manual alternatives"],
      mediumCost: ["Install electric alternatives to pneumatic systems", "Add blowers for low-pressure needs"],
      highCost: ["Complete process redesign to eliminate compressed air", "Major system conversions"]
    },
    riskFactors: ["Process changes may affect product quality", "Higher capital cost for alternatives", "Training requirements for new systems"]
  },
  "2.7135": {
    title: "Install occupancy sensors",
    description: "Install occupancy sensors to automatically control lighting in areas with intermittent occupancy.",
    keyLevers: [
      {
        category: "Instrumentation and Measurement",
        actions: [
          "Survey areas for occupancy patterns",
          "Measure existing lighting energy consumption",
          "Calculate potential savings from occupancy control"
        ]
      },
      {
        category: "Control System Upgrades",
        actions: [
          "Install PIR or ultrasonic occupancy sensors",
          "Add daylight harvesting sensors where applicable",
          "Implement time delay controls for appropriate applications"
        ]
      },
      {
        category: "Operational Practices",
        actions: [
          "Adjust sensor settings for optimal performance",
          "Train occupants on sensor operation",
          "Monitor and adjust control schedules"
        ]
      },
      {
        category: "Maintenance and Efficiency Measures",
        actions: [
          "Clean sensor lenses regularly",
          "Test sensor operation and calibration",
          "Replace failed sensors promptly"
        ]
      },
      {
        category: "Digital and Data Use",
        actions: [
          "Monitor lighting energy savings",
          "Track sensor performance and failures",
          "Analyze occupancy patterns for optimization"
        ]
      }
    ],
    costCategories: {
      lowCost: ["Install basic occupancy sensors", "Add manual override switches"],
      mediumCost: ["Networked occupancy control systems", "Daylight harvesting integration"],
      highCost: ["Complete building automation integration"]
    },
    riskFactors: ["Nuisance switching from sensor sensitivity", "Occupant complaints about lighting control", "Sensor failures leave lights on"]
  },
  "2.7142": {
    title: "Utilise higher efficiency lamps and/or ballasts",
    description: "Replace inefficient lighting with LED systems and high-efficiency ballasts to reduce lighting energy consumption.",
    keyLevers: [
      {
        category: "Instrumentation and Measurement",
        actions: [
          "Conduct lighting audit to assess existing systems",
          "Measure light levels and energy consumption",
          "Calculate payback for lighting upgrades"
        ]
      },
      {
        category: "Control System Upgrades",
        actions: [
          "Install LED lighting systems",
          "Use electronic ballasts for fluorescent lights",
          "Add lighting controls and dimming capability"
        ]
      },
      {
        category: "Operational Practices",
        actions: [
          "Optimize lighting schedules and controls",
          "Train staff on efficient lighting operation",
          "Coordinate lighting with occupancy patterns"
        ]
      },
      {
        category: "Maintenance and Efficiency Measures",
        actions: [
          "Clean lighting fixtures and lenses regularly",
          "Replace failed lamps with efficient alternatives",
          "Maintain lighting control systems"
        ]
      },
      {
        category: "Digital and Data Use",
        actions: [
          "Monitor lighting energy consumption",
          "Track lamp replacement schedules",
          "Analyze lighting usage patterns"
        ]
      }
    ],
    costCategories: {
      lowCost: ["Replace lamps with efficient alternatives", "Install electronic ballasts"],
      mediumCost: ["LED retrofit kits for existing fixtures", "New LED fixtures"],
      highCost: ["Complete lighting system redesign", "Smart lighting control systems"]
    },
    riskFactors: ["Higher upfront cost for LED systems", "Light quality changes may affect operations", "Disposal requirements for old ballasts"]
  }
};


